/**
 * Navigation configuration for CliqForms UI
 * Centralized navigation structure for the application
 */

import { helpArticles } from "@/shared/constants/help-articles";
import config from "@/api/config";

export const navigationGroups = [
  {
    title: "General",
    items: [
      {
        title: "Dashboard",
        icon: "mdi-view-dashboard",
        value: "dashboard",
        to: { name: "dashboard" },
      },
    ],
  },
  {
    title: "Core",
    items: [
      {
        title: "Forms",
        icon: "mdi-form-select",
        value: "forms",
        to: { name: "forms.index" },
        requiresFeature: "FORMS",
        lockWhenMissing: true,
        lockedTooltip: "Forms are not available on your current plan. Please upgrade or contact your administrator.",
      },
      {
        title: "QR Code Generator",
        icon: "mdi-qrcode",
        value: "qr",
        href: config.qrUrl,
        external: true,
      },
      {
        title: "Landing Pages",
        icon: "mdi-view-day",
        value: "landingpages",
        to: { name: "landingpages.index" },
        requiresFeature: "LANDING_PAGE",
        lockWhenMissing: true,
        lockedTooltip: "Landing pages are not available on your current plan. Please upgrade or contact your administrator.",
      },
      {
        title: "E-Signatures",
        icon: "mdi-file-sign",
        value: "esignatures",
        action: "openEsign",
        requiresFeature: "ESIGN",
        lockWhenMissing: true,
        lockedTooltip: "eSign is not available on your current plan. Please upgrade or contact your administrator.",
      },
    ],
  },
  {
    title: "Features",
    items: [
      {
        title: "Partial Submissions",
        icon: "mdi-account-supervisor-circle-outline",
        value: "partialleads",
        to: { name: "partialleads" },
        tooltip:
          "See where users drop off and optimize your forms to increase completion rates. Follow up with potential leads who didn't finish the process.",
        tooltipLocation: "right",
        learnMore: helpArticles.PARTIAL_LEADS,
      },
      {
        title: "Lead Proofs",
        icon: "mdi-shield-check",
        value: "leadProofs",
        to: { name: "leadproofs.index.list" },
        tooltip:
          "LeadProof boosts your website’s credibility by showing real-time pop-ups of recent form sign ups, like “John from London just signed up,” to build trust and increase engagement.",
        tooltipLocation: "right",
        learnMore: helpArticles.LEAD_PROOFS,
      },
      {
        title: "Google reCAPTCHA",
        icon: "mdi-sync-circle",
        value: "recaptcha",
        to: { name: "googlerecaptchakeys" },
        tooltip:
          "reCAPTCHA is a free service that protects your site from Spam and Abuse. It uses advanced risk analysis techniques to tell humans and bots apart.",
        tooltipLocation: "right",
        learnMore: helpArticles.RECAPTCHA,
      },
      {
        title: "UTM Tracking",
        icon: "mdi-chart-line",
        value: "utm",
        to: { name: "utmparams.index.list" },
        tooltip:
          "UTM tracking lets you add tags to your website links so you can see exactly where your leads are coming from, such as email campaigns, social media, or paid ads. This helps you understand which channels are performing best and where to focus your marketing efforts.",
        tooltipLocation: "right",
        learnMore: helpArticles.UTM_PARAMETERS,
      },
    ],
  },
  {
    title: "Integrations",
    items: [
      {
        title: "All Integrations",
        icon: "mdi-connection",
        value: "connections",
        to: { name: "connections.list" },
        tooltip:
          "Connect CliqForms forms to CRMs, email platforms, and automation tools. We support integrations via Webhooks, Contact State, and TrustedForm.",
        tooltipLocation: "right",
        learnMore: helpArticles.INTEGRATIONS,
      },
      {
        title: "Payment Settings",
        icon: "mdi-credit-card-settings-outline",
        value: "payment-settings",
        to: { name: "settings.payment" },
        tooltip:
          "Connect Stripe or PayPal once, then choose those connected accounts inside payment questions.",
        tooltipLocation: "right",
      },
    ],
  },
  {
    title: "Pricing",
    items: [
      {
        title: "Plans",
        icon: "mdi-currency-usd",
        value: "plans",
        to: { name: "plans.index" },
        badge: "Upgrade",
      },
      {
        title: "Billing",
        icon: "mdi-credit-card-settings",
        value: "billing",
        to: { name: "billing.index" },
      },
    ],
  },
  {
    title: "Admin",
    items: [
      {
        title: "Users",
        icon: "mdi-account-group",
        value: "users",
        to: { name: "users.index" },
        adminOnly: true,
      },
      {
        title: "Blacklist IPs",
        icon: "mdi-shield-off",
        value: "blacklistips",
        to: { name: "blacklist.ips" },
        adminOnly: true,
      },
      {
        title: "Template Builder",
        icon: "mdi-layers-plus",
        value: "templates",
        to: { name: "template-builder.index.list" },
        adminOnly: true,
      },
      {
        title: "Package Builder",
        icon: "mdi-briefcase-plus",
        value: "packagebuilder",
        to: { name: "package-builder.index.list" },
        adminOnly: true,
      },
      {
        title: "User Settings",
        icon: "mdi-account-cog",
        value: "user-settings",
        to: { name: "user-settings" },
        adminOnly: true,
      },
      {
        title: "CRM Connect",
        icon: "mdi-transit-connection-variant",
        value: "crm-webhooks",
        to: { name: "internal.crm-webhooks" },
        adminOnly: true,
      },
      {
        title: "Phishing Forms",
        icon: "mdi-shield-alert",
        value: "phishing-forms",
        to: { name: "phishing-forms.index" },
        adminOnly: true,
      },
    ],
  },
];

/**
 * Get all navigation items flattened
 */
export const getAllNavigationItems = () => {
  return navigationGroups.flatMap((group) => group.items);
};

/**
 * Get page title from current route
 */
export const getPageTitle = (routePath) => {
  const allItems = getAllNavigationItems();
  const item = allItems.find((item) => {
    if (item.to?.name === "dashboard") {
      return (
        routePath === "/" || routePath === "" || routePath === "/dashboard"
      );
    }
    return (
      item.to && (routePath === item.to || routePath.startsWith(item.to + "/"))
    );
  });
  return item ? item.title : "Dashboard";
};
